import React, { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { 
  Play, BookOpen, CheckCircle2, Clock, ChevronRight,
  RotateCcw, Video, X, FileText, Target, MousePointer2
} from 'lucide-react';
import { Button } from '../ui/button';
import { ScrollArea } from '../ui/scroll-area';
import { Badge } from '../ui/badge';
import {
  Dialog,
  DialogContent,
  DialogTitle,
} from '../ui/dialog';
import { InteractiveTutorial, INTERACTIVE_TUTORIALS } from './InteractiveTutorial';
import { TutorialSlideshow, TUTORIAL_CONTENT } from './TutorialSlideshow';

// Tutorial chapters - now with interactive tutorials that show real KEY-MADE interface
const TUTORIAL_CHAPTERS = [
  {
    id: 'welcome-dashboard',
    title: 'Mastering Your Dashboard',
    description: 'Navigate KEY-MADE like a professional',
    icon: '🏠',
    duration: '8 min',
    type: 'interactive',
    steps: 4,
    category: 'Getting Started',
  },
  {
    id: 'creating-projects',
    title: 'Creating & Managing Projects',
    description: 'Set up projects that organize your workflow',
    icon: '📋',
    duration: '15 min',
    type: 'interactive',
    steps: 3,
    category: 'Getting Started',
  },
  {
    id: 'clips-mastery',
    title: 'Mastering the Clip Library',
    description: 'Collect, organize, and use product clips',
    icon: '✂️',
    duration: '15 min',
    type: 'interactive',
    steps: 4,
    category: 'Design Tools',
  },
  {
    id: 'browser-extension',
    title: 'Chrome Web Clipper Extension',
    description: 'Save products from any website',
    icon: '🔌',
    duration: '5 min',
    type: 'slideshow',
    fallbackId: 'browser-extension',
    category: 'Design Tools',
  },
  {
    id: 'financials-mastery',
    title: 'Financial Management',
    description: 'Estimates, invoices, and getting paid',
    icon: '💰',
    duration: '20 min',
    type: 'interactive',
    steps: 4,
    category: 'Financials',
  },
  {
    id: 'rfp-workflow',
    title: 'RFP & Procurement Workflow',
    description: 'Request quotes and manage purchase orders',
    icon: '📦',
    duration: '15 min',
    type: 'interactive',
    steps: 3,
    category: 'Financials',
  },
  {
    id: 'change-orders',
    title: 'Managing Change Orders',
    description: 'Handle scope changes professionally',
    icon: '📝',
    duration: '10 min',
    type: 'interactive',
    steps: 3,
    category: 'Financials',
  },
  {
    id: 'contractor-management',
    title: 'Contractor Network Management',
    description: 'Build and organize your contractor network',
    icon: '👷',
    duration: '12 min',
    type: 'interactive',
    steps: 3,
    category: 'Collaboration',
  },
  {
    id: 'drawing-room',
    title: 'Client Portal (Drawing Room)',
    description: 'Share designs and get client approvals',
    icon: '🚪',
    duration: '12 min',
    type: 'interactive',
    steps: 3,
    category: 'Collaboration',
  },
  {
    id: 'documents',
    title: 'Document Library & Templates',
    description: 'Generate professional documents',
    icon: '📁',
    duration: '5 min',
    type: 'slideshow',
    fallbackId: 'documents',
    category: 'Resources',
  },
];

const STORAGE_KEY = 'keymade-tutorial-progress-v2';

const loadProgress = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    return saved ? JSON.parse(saved) : { completedChapters: [] };
  } catch {
    return { completedChapters: [] };
  }
};

const saveProgress = (data) => {
  localStorage.setItem(STORAGE_KEY, JSON.stringify(data));
};

const TourLauncher = ({ open, onOpenChange }) => {
  const navigate = useNavigate();
  const location = useLocation();
  
  const [progress, setProgress] = useState(loadProgress);
  const [activeTutorial, setActiveTutorial] = useState(null);
  const [tutorialType, setTutorialType] = useState(null);
  const [showResetConfirm, setShowResetConfirm] = useState(false);

  const completedChapters = progress.completedChapters || [];
  const totalChapters = TUTORIAL_CHAPTERS.length;

  // Group chapters by category
  const groupedChapters = {
    'Getting Started': TUTORIAL_CHAPTERS.filter(ch => ch.category === 'Getting Started'),
    'Design Tools': TUTORIAL_CHAPTERS.filter(ch => ch.category === 'Design Tools'),
    'Financials': TUTORIAL_CHAPTERS.filter(ch => ch.category === 'Financials'),
    'Collaboration': TUTORIAL_CHAPTERS.filter(ch => ch.category === 'Collaboration'),
    'Resources': TUTORIAL_CHAPTERS.filter(ch => ch.category === 'Resources'),
  };

  const handleStartTutorial = (chapter) => {
    setActiveTutorial(chapter.id);
    setTutorialType(chapter.type);
  };

  const handleTutorialComplete = (chapterId) => {
    if (!completedChapters.includes(chapterId)) {
      const newProgress = {
        ...progress,
        completedChapters: [...completedChapters, chapterId]
      };
      setProgress(newProgress);
      saveProgress(newProgress);
    }
    setActiveTutorial(null);
    setTutorialType(null);
    onOpenChange(false);
  };

  const handleResetProgress = () => {
    const newProgress = { completedChapters: [] };
    setProgress(newProgress);
    saveProgress(newProgress);
    setShowResetConfirm(false);
  };

  // Show the appropriate tutorial type
  if (activeTutorial && tutorialType === 'interactive') {
    return (
      <InteractiveTutorial
        tutorialId={activeTutorial}
        onClose={() => {
          setActiveTutorial(null);
          setTutorialType(null);
        }}
        onComplete={() => handleTutorialComplete(activeTutorial)}
        startingRoute={location.pathname}
      />
    );
  }

  if (activeTutorial && tutorialType === 'slideshow') {
    const chapter = TUTORIAL_CHAPTERS.find(ch => ch.id === activeTutorial);
    const slideshowId = chapter?.fallbackId || activeTutorial;
    return (
      <TutorialSlideshow
        chapterId={slideshowId}
        onClose={() => {
          setActiveTutorial(null);
          setTutorialType(null);
        }}
        onComplete={() => handleTutorialComplete(activeTutorial)}
        startingRoute={location.pathname}
      />
    );
  }

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-4xl max-h-[85vh] p-0 overflow-hidden" aria-describedby="tutorial-library-desc">
        <DialogTitle className="sr-only">KEY-MADE Tutorial Library</DialogTitle>
        
        {/* Header */}
        <div className="bg-gradient-to-r from-[#70170F] to-[#8B1E14] px-6 py-5 text-white">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-3">
              <div className="w-12 h-12 rounded-xl bg-white/20 flex items-center justify-center">
                <Target className="w-6 h-6" />
              </div>
              <div>
                <h2 className="text-xl font-bold">KEY-MADE Training Academy</h2>
                <p className="text-white/80 text-sm">
                  Interactive tutorials with real interface walkthroughs
                </p>
              </div>
            </div>
            <div className="text-right">
              <div className="text-2xl font-bold">{completedChapters.length}/{totalChapters}</div>
              <div className="text-xs text-white/70">Tutorials Mastered</div>
            </div>
          </div>
          
          {completedChapters.length > 0 && (
            <div className="mt-4">
              <div className="flex items-center justify-between text-xs text-white/80 mb-1">
                <span>Your Progress</span>
                <span>{Math.round((completedChapters.length / totalChapters) * 100)}% Complete</span>
              </div>
              <div className="h-2 bg-white/20 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-white rounded-full transition-all duration-500"
                  style={{ width: `${(completedChapters.length / totalChapters) * 100}%` }}
                />
              </div>
            </div>
          )}
        </div>

        <ScrollArea className="max-h-[calc(85vh-180px)]">
          <div className="p-6 space-y-6">
            {/* Introduction */}
            <div className="bg-gradient-to-r from-blue-50 to-indigo-50 dark:from-blue-900/20 dark:to-indigo-900/20 border border-blue-200 dark:border-blue-800 rounded-xl p-5">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 rounded-full bg-blue-500 text-white flex items-center justify-center flex-shrink-0">
                  <MousePointer2 className="w-6 h-6" />
                </div>
                <div>
                  <h3 className="font-bold text-gray-900 dark:text-white text-lg mb-1">
                    Interactive Training Experience
                  </h3>
                  <p className="text-sm text-gray-600 dark:text-gray-400 mb-3">
                    These aren't generic videos - they show the <strong>actual KEY-MADE interface</strong> with animated cursor movements, 
                    highlighted areas, and step-by-step explanations. Watch exactly where to click and what happens when you do.
                  </p>
                  <div className="flex items-center gap-4 text-xs">
                    <span className="flex items-center gap-1 text-blue-600">
                      <Target className="w-4 h-4" />
                      Real screenshots
                    </span>
                    <span className="flex items-center gap-1 text-blue-600">
                      <MousePointer2 className="w-4 h-4" />
                      Animated cursor
                    </span>
                    <span className="flex items-center gap-1 text-blue-600">
                      <FileText className="w-4 h-4" />
                      Detailed explanations
                    </span>
                  </div>
                </div>
              </div>
            </div>

            {/* Quick Start */}
            <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 border border-amber-200 dark:border-amber-800 rounded-xl p-5">
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-amber-500 text-white flex items-center justify-center">
                    🚀
                  </div>
                  <div>
                    <h3 className="font-bold text-gray-900 dark:text-white">
                      {completedChapters.length === 0 ? 'Begin Your Training' : 'Continue Training'}
                    </h3>
                    <p className="text-sm text-gray-600 dark:text-gray-400">
                      {completedChapters.length === 0 
                        ? 'Start with the Dashboard tutorial to learn KEY-MADE basics'
                        : `${totalChapters - completedChapters.length} tutorials remaining`
                      }
                    </p>
                  </div>
                </div>
                <Button 
                  onClick={() => {
                    const nextChapter = completedChapters.length === 0 
                      ? TUTORIAL_CHAPTERS[0]
                      : TUTORIAL_CHAPTERS.find(ch => !completedChapters.includes(ch.id)) || TUTORIAL_CHAPTERS[0];
                    handleStartTutorial(nextChapter);
                  }}
                  className="bg-[#70170F] hover:bg-[#8B1E14] text-white px-6"
                >
                  <Play className="w-4 h-4 mr-2" />
                  {completedChapters.length === 0 ? 'Start Training' : 'Continue'}
                </Button>
              </div>
            </div>

            {/* Chapter Groups */}
            {Object.entries(groupedChapters).map(([groupName, groupChapters]) => (
              <div key={groupName}>
                <h3 className="text-sm font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-3">
                  {groupName}
                </h3>
                <div className="grid gap-3">
                  {groupChapters.map((chapter) => {
                    const isCompleted = completedChapters.includes(chapter.id);
                    const isInteractive = chapter.type === 'interactive';
                    const hasContent = isInteractive 
                      ? INTERACTIVE_TUTORIALS[chapter.id] 
                      : TUTORIAL_CONTENT[chapter.fallbackId || chapter.id];
                    
                    return (
                      <div
                        key={chapter.id}
                        className={`p-4 rounded-xl border transition-all ${
                          isCompleted 
                            ? 'bg-green-50 dark:bg-green-900/20 border-green-200 dark:border-green-800' 
                            : 'bg-white dark:bg-gray-800 border-gray-200 dark:border-gray-700 hover:border-[#70170F] hover:shadow-md'
                        }`}
                      >
                        <div className="flex items-center gap-4">
                          <div className={`w-14 h-14 rounded-xl flex items-center justify-center text-2xl ${
                            isCompleted 
                              ? 'bg-green-100 dark:bg-green-900/40' 
                              : 'bg-gray-100 dark:bg-gray-700'
                          }`}>
                            {chapter.icon}
                          </div>
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <h4 className="font-semibold text-gray-900 dark:text-white">
                                {chapter.title}
                              </h4>
                              {isCompleted && (
                                <Badge variant="secondary" className="text-xs bg-green-100 text-green-700">
                                  <CheckCircle2 className="w-3 h-3 mr-1" />
                                  Mastered
                                </Badge>
                              )}
                              {isInteractive && !isCompleted && (
                                <Badge variant="secondary" className="text-xs bg-blue-100 text-blue-700">
                                  <MousePointer2 className="w-3 h-3 mr-1" />
                                  Interactive
                                </Badge>
                              )}
                            </div>
                            <p className="text-sm text-gray-500 dark:text-gray-400 mt-0.5">
                              {chapter.description}
                            </p>
                            <div className="flex items-center gap-4 mt-2">
                              <span className="flex items-center gap-1 text-xs text-gray-400">
                                <Clock className="w-3 h-3" />
                                {chapter.duration}
                              </span>
                              {chapter.steps && (
                                <span className="flex items-center gap-1 text-xs text-gray-400">
                                  <Target className="w-3 h-3" />
                                  {chapter.steps} interactive steps
                                </span>
                              )}
                            </div>
                          </div>
                          <div className="flex-shrink-0">
                            <Button
                              onClick={() => handleStartTutorial(chapter)}
                              disabled={!hasContent}
                              className={isCompleted 
                                ? "bg-gray-200 hover:bg-gray-300 text-gray-700" 
                                : "bg-[#70170F] hover:bg-[#8B1E14] text-white"
                              }
                            >
                              {isInteractive ? (
                                <MousePointer2 className="w-4 h-4 mr-2" />
                              ) : (
                                <FileText className="w-4 h-4 mr-2" />
                              )}
                              {isCompleted ? 'Review' : 'Start'}
                            </Button>
                          </div>
                        </div>
                      </div>
                    );
                  })}
                </div>
              </div>
            ))}

            {/* Reset Progress */}
            {completedChapters.length > 0 && (
              <div className="pt-4 border-t border-gray-200 dark:border-gray-700">
                {!showResetConfirm ? (
                  <button
                    onClick={() => setShowResetConfirm(true)}
                    className="text-sm text-gray-500 hover:text-[#70170F] flex items-center gap-2"
                  >
                    <RotateCcw className="w-4 h-4" />
                    Reset all progress
                  </button>
                ) : (
                  <div className="flex items-center gap-3 p-3 bg-red-50 dark:bg-red-900/20 rounded-lg">
                    <p className="text-sm text-red-700 dark:text-red-300 flex-1">
                      This will reset all your training progress. Are you sure?
                    </p>
                    <Button variant="outline" size="sm" onClick={() => setShowResetConfirm(false)}>
                      Cancel
                    </Button>
                    <Button variant="destructive" size="sm" onClick={handleResetProgress}>
                      Reset
                    </Button>
                  </div>
                )}
              </div>
            )}
          </div>
        </ScrollArea>
      </DialogContent>
    </Dialog>
  );
};

export default TourLauncher;
